import axios from "axios";
import { readDetailCache, withDetailCache, writeDetailCache } from "./detailCache";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

const PUBLIC_AUTH_401_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/register/confirm-2fa",
  "/auth/register/resend-2fa",
  "/auth/verify-email",
  "/auth/verify-2fa",
  "/auth/resend-2fa",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/confirm-reset-password",
  "/auth/resend-reset-password-2fa",
  "/auth/logout",
];

let sessionExpiredHandler = null;

export const setSessionExpiredHandler = (handler) => {
  sessionExpiredHandler = handler;
};

const isPublicAuthRequest = (url = "") =>
  PUBLIC_AUTH_401_PATHS.some((path) => String(url).includes(path));

// Board searches also return 401 when a platform token is stale. Those must
// stay on the page. Only the user-session check uses this code.
const isUserSessionExpired = (error) =>
  error.response?.data?.code === "SESSION_EXPIRED";

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    if (
      status === 401 &&
      isUserSessionExpired(error) &&
      sessionExpiredHandler &&
      !isPublicAuthRequest(url)
    ) {
      sessionExpiredHandler(error);
    }
    return Promise.reject(error);
  }
);

const handleApiError = (error, callback) => {
  if (callback) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An error occurred";
    callback(errorMessage);
  } else {
    console.error(
      "API Error:",
      error.response?.data?.errorLog ||
        error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        error
    );
  }
};

const rejectPlatformError = (error, errorCallback) => {
  handleApiError(error, errorCallback);
  return Promise.reject(error);
};

export const verifyUser = async () => {
  return await api.get("/auth/check");
};

export const register = async ({
  name,
  email,
  password,
  type,
  phoneNumber,
  consentAcknowledged,
}) => {
  try {
    const response = await api.post("/auth/register", {
      name,
      email,
      password,
      type,
      phoneNumber,
      consentAcknowledged,
    });
    return { status: true, data: response.data };
  } catch (error) {
    return {
      status: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

export const confirmRegister2FA = async (setupToken, code, phoneNumber) => {
  return await api.post("/auth/register/confirm-2fa", {
    setupToken,
    code,
    phoneNumber,
  });
};

export const resendRegister2FA = async (setupToken) => {
  return await api.post("/auth/register/resend-2fa", { setupToken });
};

export const requestPasswordReset = async (email) => {
  return await api.post("/auth/forgot-password", { email });
};

export const startPasswordReset = async (token, newPassword) => {
  return await api.post("/auth/reset-password", { token, newPassword });
};

export const confirmPasswordReset = async (resetToken, code) => {
  return await api.post("/auth/confirm-reset-password", { resetToken, code });
};

export const resendResetPassword2FA = async (resetToken) => {
  return await api.post("/auth/resend-reset-password-2fa", { resetToken });
};

export const login = async (email, password) => {
  return await api.post("/auth/login", {
    email,
    password,
  });
};

export const logout = async () => {
  return await api.post("/auth/logout");
};

// 2FA API calls
export const verify2FA = async (tempToken, code) => {
  return await api.post("/auth/verify-2fa", { tempToken, code });
};

export const resend2FACode = async (tempToken) => {
  return await api.post("/auth/resend-2fa", { tempToken });
};

export const enableTwoFA = async (phoneNumber, consentAcknowledged = true) => {
  return await api.post("/auth/enable-2fa", { phoneNumber, consentAcknowledged });
};

export const confirmEnable2FA = async (phoneNumber, code) => {
  return await api.post("/auth/confirm-enable-2fa", { phoneNumber, code });
};

export const disableTwoFA = async () => {
  return await api.post("/auth/disable-2fa");
};

export const updatePhoneNumber = async (phoneNumber, consentAcknowledged = true) => {
  return await api.post("/auth/update-phone", {
    phoneNumber,
    consentAcknowledged,
  });
};

export const confirmUpdatePhone = async (phoneNumber, code) => {
  return await api.post("/auth/confirm-update-phone", { phoneNumber, code });
};

export const changePassword = async (newPassword) => {
  return await api.post("/auth/change-password", {
    newPassword,
  });
};

export const confirmChangePassword = async (changeToken, code) => {
  return await api.post("/auth/confirm-change-password", {
    changeToken,
    code,
  });
};

export const resendChangePassword2FA = async (changeToken) => {
  return await api.post("/auth/resend-change-password-2fa", { changeToken });
};

export const getSessions = async () => {
  return await api.get("/auth/sessions");
};

export const revokeSession = async (sessionId) => {
  return await api.delete(`/auth/sessions/${sessionId}`);
};

export const logoutEverywhere = async () => {
  return await api.post("/auth/logout-everywhere");
};

const postPlatformSearch = (url, data, errorCallback, options = {}) => {
  const config = options?.signal ? { signal: options.signal } : undefined;
  return api
    .post(url, { data }, config)
    .catch((error) => rejectPlatformError(error, errorCallback));
};

export const graphQL = (data, errorCallback, options = {}) => {
  return postPlatformSearch("/dat/graphql", data, errorCallback, options);
};

export const matchDetails = (data, errorCallback) => {
  const matchId = data?.matchId;
  const load = () =>
    api
      .post("/dat/matchDetails", { data })
      .then((response) => ({ data: response.data }))
      .catch((error) => {
        handleApiError(error, errorCallback);
        return null;
      });

  if (!matchId) return load();
  return withDetailCache(`dat-detail:${String(matchId)}`, load);
};

export const similarLocation = (data, errorCallback) => {
  return api
    .post("/dat/similarLocation", { data })
    .catch((error) => handleApiError(error, errorCallback));
};

export const searchLocations = (text, errorCallback) => {
  return api
    .get("/locations/search", { params: { text } })
    .catch((error) => handleApiError(error, errorCallback));
};

export const getParadePartners = (errorCallback) => {
  return api
    .get("/parade/partners")
    .catch((error) => handleApiError(error, errorCallback));
};

export const getParadeTabLoads = (params = {}, errorCallback) => {
  return api
    .get("/parade/parade-tab-loads", { params })
    .catch((error) => handleApiError(error, errorCallback));
};

export const searchParadeTabLoads = (params = {}, errorCallback) => {
  return api
    .get("/parade/parade-tab-loads/search", { params })
    .catch((error) => handleApiError(error, errorCallback));
};

export const paradeBookNow = (loadId, bidId) => {
  return api.post(
    `/parade/parade-detail-infos/book/${encodeURIComponent(loadId)}`,
    bidId ? { bidId } : {}
  );
};

export const similarCompany = (data, errorCallback) => {
  return api
    .post("/dat/similarCompany", { data })
    .catch((error) => handleApiError(error, errorCallback));
};

export const getRouteData = async (locationOrg, locationDes, errorCallback) => {
  try {
    const response = await api.post("/dat/getRouteData", {
      locationOrg,
      locationDes,
    });
    return response.data;
  } catch (error) {
    handleApiError(error, errorCallback);
    throw error;
  }
};

export const deleteSearchTab = (id) => api.delete(`/search_tabs/${id}`);

export const createSearchTab = (filters, { markSearched = false } = {}) =>
  api.post(`/search_tabs/`, filters, {
    params: markSearched ? { searched: 1 } : undefined,
  });

export const updateSearchTab = (id, filters, { markSearched = false } = {}) =>
  api.patch(`/search_tabs/${id}`, filters, {
    params: markSearched ? { searched: 1 } : undefined,
  });

export const getSearchTabs = (_userId, kind) =>
  api.get("/search_tabs", {
    params: kind ? { kind } : undefined,
  });

export const getSearchTabHistory = (limit = 10) =>
  api.get("/search_tabs/history", {
    params: { limit },
  });

export const getAllUsers = async () => {
  try {
    return await api.get("/users");
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const createAdminUser = async (email, user_type) => {
  try {
    return await api.post("/users", { email, user_type });
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getUserDetails = async (id) => {
  try {
    return await api.get(`/users/${id}`);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const updateUserDetails = async (id, data) => {
  try {
    return await api.patch(`/users/${id}`, data);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const setUserCanBid = async (id, can_bid) => {
  try {
    return await api.patch(`/users/canBid/${id}`, { can_bid });
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const revokeUserSession = async (userId, sessionId) => {
  try {
    return await api.delete(`/users/${userId}/sessions/${sessionId}`);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const revokeAllUserSessions = async (userId) => {
  try {
    return await api.post(`/users/${userId}/revoke-sessions`);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// Admin Page Permissions --------------
export const approveUser = async (id) => {
  return api
    .patch(`/users/${id}`, { is_verified: true })
    .catch((error) => handleApiError(error));
};

export const rejectUser = async (id) => {
  return api
    .patch(`/users/${id}`, { is_verified: false })
    .catch((error) => handleApiError(error));
};

export const allowBid = async (id) => {
  return api
    .patch(`/users/canBid/${id}`, { can_bid: true })
    .catch((error) => handleApiError(error));
};

export const disableBid = async (id) => {
  return api
    .patch(`/users/canBid/${id}`, { can_bid: false })
    .catch((error) => handleApiError(error));
};

export const deleteUser = async (id) => {
  try {
    return await api.delete(`/users/${id}`);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const updateUserRole = async (id, user_type) => {
  try {
    return await api.patch(`/users/${id}`, { user_type });
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const saveLoadDetails = async (loadData) => {
  try {
    const response = await api.post("/bid/load", loadData);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const saveBidDetails = async (bidData) => {
  try {
    const response = await api.post("/bid/userBid", bidData);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getBid = async (matchId) => {
  try {
    const response = await api.post("/bid/getBid", { matchId });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const updateBid = async (bidId, status) => {
  try {
    const response = await api.post("/bid/updateBid", { bidId, status });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const updateBidRequest = async (bidId, status, bidAmount) => {
  try {
    const response = await api.post("/bid/updateBidRequest", {
      bidId,
      status,
      bidAmount,
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const acceptBid = async (id) => {
  return api
    .post(`/bid/acceptBid/${id}`)
    .catch((error) => handleApiError(error));
};

export const rejectBid = async (id) => {
  return api
    .post(`/bid/rejectBid/${id}`)
    .catch((error) => handleApiError(error));
};

export const requestBid = async (id, requestedAmount, requestedBy) => {
  return api
    .post(`/bid/requestBid/${id}`, { requestedAmount, requestedBy })
    .catch((error) => handleApiError(error));
};

export const cancelBid = async (id) => {
  return api
    .post(`/bid/cancelBid/${id}`)
    .catch((error) => handleApiError(error));
};

export const getBidList = async (userId = null) => {
  try {
    const response = userId
      ? await api.post("/bid/getBidList", { userId })
      : await api.get("/bid/getBidList");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

//get specific load data(Id)
export const getMatchLoadData = async (loadId) => {
  return api.post("/bid/getMatchLoadData", { loadId });
};

export const saveBidAlert = async (bidAlert) => {
  return api
    .post("/bid/saveBidAlert", bidAlert)
    .catch((error) => handleApiError(error));
};

export const getBidAlertShowFlag = async () => {
  return api
    .get("/bid/getBidAlertShowFlag")
    .catch((error) => handleApiError(error));
};

export const getBidShowAlertList = async (alertFlagsFilterData) => {
  return api
    .post("/bid/getBidShowAlertList", { alertFlagsFilterData })
    .catch((error) => handleApiError(error));
};

export const unSetBadgeNums = async (userId) => {
  return api
    .post("/bid/unSetBadgeNums", { userId })
    .catch((error) => handleApiError(error));
};

export const saveBidAlertShowFlag = async (alertId) => {
  return api
    .post("/bid/saveBidAlertShowFlag", { alertId })
    .catch((error) => handleApiError(error));
};

export const mciGraphql = async (payload) => {
  return api
    .post("/market/mci", { payload })
    .catch((error) => handleApiError(error));
};

export const loadTruckGraphql = async (payload) => {
  return api
    .post("/market/loadTruck", { payload })
    .catch((error) => handleApiError(error));
};

export const MCIGraph = async (payload) => {
  return api
    .post("/market/mciGraph", { payload })
    .catch((error) => handleApiError(error));
};

export const LTGraph = async (payload) => {
  return api
    .post("/market/LTGraph", { payload })
    .catch((error) => handleApiError(error));
};

export const smiliarAreaName = async (payload) => {
  return api
    .post("/market/findAreaName", { payload })
    .catch((error) => handleApiError(error));
};

// Echo Platform

export const graphQLEcho = (data, errorCallback, options = {}) => {
  return postPlatformSearch("/echo/graphql", data, errorCallback, options);
};

const echoPriceCacheKey = (loadId) => `echo-price:${String(loadId)}`;
const ECHO_CONTACT_CACHE_KEY = "echo-contact";
const ECHO_PRICE_REQUEST_CHUNK = 20;

/** Only the fields the Echo book-now price call reads. Full search rows 413. */
const toEchoPriceRequestItem = (load) => {
  const equipment = Array.isArray(load?.equipment) ? load.equipment : [];
  const specialServices = Array.isArray(load?.specialServices)
    ? load.specialServices
    : [];
  const loadStops = Array.isArray(load?.loadStops) ? load.loadStops : [];

  return {
    loadId: load?.loadId,
    loadedMiles: load?.loadedMiles ?? null,
    customerKey: load?.customerKey ?? null,
    equipment: equipment.map((entry) => ({
      truckType: entry?.truckType || null,
    })),
    specialServices: specialServices.map((service) => ({
      name: typeof service === "string" ? service : service?.name || null,
    })),
    loadStops: loadStops.map((stop) => ({
      stopOrder: stop?.stopOrder ?? null,
      stopType: stop?.stopType ?? null,
      city: stop?.city ?? stop?.location?.city ?? null,
      state: stop?.state ?? stop?.location?.state ?? null,
      postalCode: stop?.postalCode ?? stop?.location?.postalCode ?? null,
      countryCode: stop?.countryCode ?? null,
      appointmentTime: {
        startTime: stop?.appointmentTime?.startTime ?? null,
        endTime: stop?.appointmentTime?.endTime ?? null,
      },
      location: {
        lat: stop?.location?.lat ?? null,
        lon: stop?.location?.lon ?? null,
      },
    })),
  };
};

/** Book-now prices and rep contact for loads already returned by search. */
export const fetchEchoPricing = async (payload, options = {}) => {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const onChunk = typeof options.onChunk === "function" ? options.onChunk : null;
  const cachedPrices = [];
  const missing = [];

  items.forEach((item) => {
    const loadId = item?.loadId;
    if (loadId == null) return;
    const hit = readDetailCache(echoPriceCacheKey(loadId));
    if (hit) cachedPrices.push(hit);
    else missing.push(item);
  });

  const cachedContact = readDetailCache(ECHO_CONTACT_CACHE_KEY);
  if (cachedPrices.length > 0 && onChunk) {
    onChunk({ prices: cachedPrices, contact: cachedContact || null });
  }
  if (missing.length === 0 && cachedContact !== undefined) {
    return { data: { prices: cachedPrices, contact: cachedContact || null } };
  }

  const config = options?.signal ? { signal: options.signal } : undefined;
  const chunks = [];
  for (let index = 0; index < missing.length; index += ECHO_PRICE_REQUEST_CHUNK) {
    chunks.push(
      missing
        .slice(index, index + ECHO_PRICE_REQUEST_CHUNK)
        .map(toEchoPriceRequestItem)
    );
  }

  const byId = new Map(cachedPrices.map((row) => [String(row.loadId), row]));
  let contact = cachedContact || null;

  await Promise.all(
    chunks.map(async (chunk) => {
      const response = await api.post(
        "/echo/pricing",
        {
          searchFileKey: payload?.searchFileKey || null,
          items: chunk,
        },
        config
      );
      const prices = Array.isArray(response.data?.prices)
        ? response.data.prices
        : [];
      prices.forEach((row) => {
        if (row?.loadId == null) return;
        writeDetailCache(echoPriceCacheKey(row.loadId), row);
        byId.set(String(row.loadId), row);
      });
      if (response.data?.contact) {
        contact = response.data.contact;
        writeDetailCache(ECHO_CONTACT_CACHE_KEY, contact);
      }
      if (onChunk && prices.length > 0) {
        onChunk({ prices, contact });
      }
    })
  );

  return { data: { prices: [...byId.values()], contact } };
};

export const placeDatAction = (bidData) =>
  api.post("/dat/place", bidData).catch((error) => {
    handleApiError(error);
    return Promise.reject(error);
  });

export const placeSchneiderBook = (bidData) =>
  api.post("/schneider/book", bidData).catch((error) => {
    handleApiError(error);
    return Promise.reject(error);
  });

// Echo Platform Bid API (Bids-page accept → Echo)
export const realEchoBidding = (bidData) => {
  return api
    .post("/echo/submit-bid", bidData)
    .catch((error) => handleApiError(error));
};

/** Dispatcher dashboard: bid or book directly on Echo. */
export const echoDispatcherAction = (payload) => {
  return api
    .post("/echo/dispatcher-action", payload)
    .catch((error) => {
      handleApiError(error);
      return Promise.reject(error);
    });
};

/** Latest Echo dispatcher bid/book records for the given load ids. */
export const getEchoDispatcherActions = (loadIds = []) => {
  const params = new URLSearchParams();
  (Array.isArray(loadIds) ? loadIds : [loadIds])
    .map((id) => String(id || "").trim())
    .filter(Boolean)
    .forEach((id) => params.append("loadIds", id));

  return api
    .get(`/echo/dispatcher-actions?${params.toString()}`)
    .catch((error) => {
      handleApiError(error);
      return Promise.reject(error);
    });
};

// Arrive Platform

export const graphQLArrive = (data, errorCallback, options = {}) => {
  return postPlatformSearch("/arrive/graphql", data, errorCallback, options);
};

export const similarArriveLocation = (data, errorCallback) => {
  return api
    .post("/arrive/similarLocation", { data })
    .catch((error) => handleApiError(error, errorCallback));
};

export const getArriveDetails = async (loadId) => {
  return api
    .post("/arrive/getLoadDetails", { loadId })
    .catch((error) => handleApiError(error));
};

//Armstrong Platform
export const graphQLArmstrong = (data, errorCallback, options = {}) => {
  return postPlatformSearch("/armstrong/graphql", data, errorCallback, options);
};

//Flockfreight Platform
export const graphQLFlockfreight = (data, errorCallback, options = {}) => {
  return postPlatformSearch(
    "/flockfreight/graphql",
    data,
    errorCallback,
    options
  );
};

//Truckstop Platform
export const graphQLTruckstop = (data, errorCallback, options = {}) => {
  return postPlatformSearch("/truckstop/graphql", data, errorCallback, options);
};

const truckstopBrokerAuthorityCacheKey = (load) => {
  const mc = String(load?.brokerMC || "").replace(/\D/g, "");
  const name = String(load?.accountName || "")
    .trim()
    .toLowerCase();
  if (mc) return `truckstop-broker:mc:${mc}`;
  if (name) return `truckstop-broker:name:${name}`;
  return "";
};

/** Broker authority for an opened TruckStop load. Cached 30 minutes. */
export const fetchTruckstopBrokerAuthority = (load, errorCallback) => {
  const key = truckstopBrokerAuthorityCacheKey(load);
  const request = () =>
    api
      .post("/truckstop/broker-authority", { data: { load } })
      .then((response) => response.data)
      .catch((error) => {
        handleApiError(error, errorCallback);
        return Promise.reject(error);
      });

  if (!key) return request();
  return withDetailCache(key, request);
};

const truckstopLaneCacheKey = (data) => {
  try {
    return `truckstop-lane:${JSON.stringify(data || null)}`;
  } catch {
    return "";
  }
};

export const getTruckstopBookedRateEstimate = (data, errorCallback) => {
  const key = truckstopLaneCacheKey(data);
  const load = () =>
    api
      .post("/truckstop/booked-rate-estimate", { data })
      .then((response) => ({ data: response.data }))
      .catch((error) => {
        handleApiError(error, errorCallback);
        return Promise.reject(error);
      });

  if (!key) return load();
  return withDetailCache(key, load);
};

getTruckstopBookedRateEstimate.peek = (data) =>
  readDetailCache(truckstopLaneCacheKey(data));

//TQL Platform
export const graphQLTQL = (data, errorCallback, options = {}) => {
  return postPlatformSearch("/tql/graphql", data, errorCallback, options);
};

//Hub Platform
export const graphQLHub = (data, errorCallback, options = {}) => {
  return postPlatformSearch("/hub/graphql", data, errorCallback, options);
};

export const getHubLoadDetails = (loadId) =>
  withDetailCache(`hub-detail:${String(loadId)}`, () =>
    api
      .get(`/hub/getLoadDetails/${loadId}`)
      .then((response) => ({ data: response.data }))
      .catch((error) => {
        handleApiError(error);
        return null;
      })
  );

export const peekHubLoadDetails = (loadId) =>
  readDetailCache(`hub-detail:${String(loadId)}`);

//LoadSmart Platform
export const graphQLLoadSmart = (data, errorCallback, options = {}) => {
  return postPlatformSearch("/loadsmart/graphql", data, errorCallback, options);
};

// LoadSmart Bid API
export const realLoadSmartBidding = async (proposalData) => {
  return api
    .post("/loadsmart/contract-proposal", proposalData)
    .catch((error) => handleApiError(error));
};

//Schneider Platform
export const graphQLSchneider = (data, errorCallback, options = {}) => {
  return postPlatformSearch("/schneider/graphql", data, errorCallback, options);
};

export const isRequestCanceled = (error) =>
  Boolean(
    error?.code === "ERR_CANCELED" ||
      error?.name === "CanceledError" ||
      error?.name === "AbortError"
  );


//Real Arrive Bidding
export const realArriveBidding = async (biddingData) => {
  return api
    .post("/arrive/realBidding", { biddingData })
    .catch((error) => handleApiError(error));
};

//Real TQL Bidding
export const realTQLBidding = async (biddingData) => {
  return api
    .post("/tql/submit-bid", biddingData)
    .catch((error) => handleApiError(error));
};

//Real Hub Bidding
export const realHubBidding = async (biddingData) => {
  return api
    .post("/hub/realBidding", { biddingData })
    .catch((error) => handleApiError(error));
};

//Real Flockfreight Bidding
export const realFlockfreightBidding = async (biddingData) => {
  return api
    .post("/flockfreight/realBidding", { biddingData })
    .catch((error) => handleApiError(error));
};

export const getBoardHealth = async () => {
  try {
    return await api.get("/ops/boards");
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const nudgeBoardRefresh = async (platform) => {
  try {
    return await api.post(`/ops/boards/${platform}/nudge`);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const updateBoardJobSettings = async (platform, payload) => {
  try {
    return await api.patch(`/ops/boards/${platform}/settings`, payload);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getBoardJobHistory = async (platform, { limit = 12 } = {}) => {
  try {
    return await api.get(`/ops/boards/${platform}/jobs`, {
      params: { limit },
    });
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};